import { Directive, inject, Input } from '@angular/core';
import { DatePickerComponent } from '@progress/kendo-angular-dateinputs';
import { Changeable } from '@technocom/common';
import type { NgChangeMap } from '@technocom/common/platform';
import { isWithinInterval } from 'date-fns';
import type { AbstractControl } from '@angular/forms';
import { DateValidators } from '@app/shared/util/validators';

const MIN_DATE = new Date(1910, 0, 1);
const MAX_DATE = new Date(2099, 11, 31);

@Directive({
  // eslint-disable-next-line @angular-eslint/directive-selector
  selector: 'kendo-datepicker',
})
export class DatePickerOptionsDirective extends Changeable {
  @Input()
  min: Date;

  @Input()
  max: Date;

  constructor() {
    super();

    const widget = this.widget;

    // если через шаблон передать min/max равное undefined/null, то kendo использует эти значения вместо стандартных мин./макс. дат,
    // поэтому нужно провалидировать вручную
    const originalValidateFn = widget.validate;

    widget.validate = (control: AbstractControl) => {
      return (
        originalValidateFn.call(widget, control) ||
        DateValidators.min(MIN_DATE)(control) ||
        DateValidators.max(MAX_DATE)(control)
      );
    };

    widget.incompleteDateValidation = true;
    widget.ngOnChanges({ incompleteDateValidation: true });
  }

  protected widget = inject(DatePickerComponent, { host: true });

  /**
   * Исправляет ситуацию, когда при открытии календаря показывается empty календарь.
   *
   * @protected
   */
  protected get focusedDate(): Date {
    const widget = this.widget;

    if (widget.value) {
      return widget.value;
    }

    const min = this.min;
    const max = this.max;
    const today = new Date();

    switch (true) {
      case !!min && !max:
        return today > min ? today : min;

      case !min && !!max:
        return max > today ? today : max;

      case !!min && !!max:
        const start = max > min ? min : max;
        const end = start === min ? max : min;

        return isWithinInterval(today, { start, end }) ? today : min;

      default:
        return today;
    }
  }

  protected override onChanges(changes: NgChangeMap): void {
    super.onChanges(changes);

    if (this.min || this.max) {
      this.widget.focusedDate = this.focusedDate;
    }
  }
}
